// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@keller/App/Components/Notify";
import { CustomAppState } from "@keller/App/Store/Reducers";
import {
    getReorderPad,
    getReorderPadIsLoading,
    getReorderPadProducts,
} from "@keller/App/Store/Selectors/ReorderPadSelectors";
import ReorderPadGrid from "@keller/App/Widgets/ReorderPad/ReorderPadGrid";
import ReorderPadTable from "@keller/App/Widgets/ReorderPad/ReorderPadTable";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

interface ReorderPadProductListStyles {
    container?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

const reorderPadProductListStyles: ReorderPadProductListStyles = {
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
            justify-content: center;
        `,
    },
    container: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = reorderPadProductListStyles;
const ReorderPadProductList: FC<Props> = () => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const { products, failedMessage, isLoading, view } = useAppSelector(state => {
        return {
            products: getReorderPadProducts(state),
            isLoading: getReorderPadIsLoading(state),
            failedMessage: getReorderPad(state).failedMessage,
            view: getReorderPad(state).view,
        };
    });

    if (isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (!products || products.length === 0) {
        return (
            <StyledWrapper {...styles.container}>
                <Notify type={failedMessage ? "danger" : "info"}>{failedMessage || translate("No Orders.")}</Notify>
            </StyledWrapper>
        );
    }

    if (isTabletScreen) {
        return (
            <StyledWrapper {...styles.container}>
                <ReorderPadGrid products={products} />
            </StyledWrapper>
        );
    }
    return (
        <StyledWrapper {...styles.container}>
            {view === "Table" ? <ReorderPadTable products={products} /> : <ReorderPadGrid products={products} />}
        </StyledWrapper>
    );
};

export default ReorderPadProductList;
